import React, { useState, useEffect, useMemo } from 'react';
import { PublicHeader, PublicFooter, ArticleCard } from '../components/PublicLayout';
import { categoriesAPI, subscriptionsAPI, publicAPI } from '../lib/api';
import { useEdition } from '../lib/edition-context';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { TrendingUp, Clock, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const DEFAULT_HOME_LAYOUT = {
  show_hero: true,
  show_secondary_strip: true,
  show_latest: true,
  show_trending_sidebar: true,
  show_category_rails: true,
  category_rails_count: 3,
  hero_source: 'featured',
  latest_limit: 8,
  trending_limit: 5,
};

export default function HomePage() {
  const { edition } = useEdition();
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [trending, setTrending] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryArticles, setCategoryArticles] = useState({});
  const [layout, setLayout] = useState(DEFAULT_HOME_LAYOUT);
  const [loading, setLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');

  const editionParam = edition && edition !== 'global' ? edition : undefined;

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edition]);

  const loadData = async () => {
    try {
      const layoutRes = await publicAPI.homeLayout().catch(() => ({}));
      const L = { ...DEFAULT_HOME_LAYOUT, ...(layoutRes.data?.layout || {}) };
      setLayout(L);

      // Use allSettled so a single transient failure (e.g. a race on cold
      // refresh) can't blank the whole page — partial data is better than
      // the "No articles" empty-state message.
      const [featRes, latestRes, trendRes, catRes] = await Promise.allSettled([
        publicAPI.articles({
          is_featured: true,
          limit: 3,
          edition: editionParam,
        }),
        publicAPI.articles({
          limit: L.latest_limit || 8,
          edition: editionParam,
        }),
        publicAPI.articles({
          is_trending: true,
          limit: L.trending_limit || 5,
          edition: editionParam,
        }),
        categoriesAPI.list(),
      ]);
      const ok = (r, path) => (r.status === 'fulfilled' ? r.value?.data?.[path] || [] : []);
      setFeatured(ok(featRes, 'articles'));
      setLatest(ok(latestRes, 'articles'));
      setTrending(ok(trendRes, 'articles'));
      setCategories(ok(catRes, 'categories'));

      const railN = Math.min(8, Math.max(0, L.category_rails_count ?? 3));
      const cats = ok(catRes, 'categories').slice(0, railN);
      const catArticleMap = {};
      for (const cat of cats) {
        try {
          const r = await publicAPI.articles({
            category_slug: cat.slug,
            limit: 4,
            edition: editionParam,
          });
          catArticleMap[cat.slug] = r.data.articles || [];
        } catch {}
      }
      setCategoryArticles(catArticleMap);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    try {
      await subscriptionsAPI.subscribe({ email: newsletterEmail, categories: [] });
      toast.success('Successfully subscribed!');
      setNewsletterEmail('');
    } catch {
      toast.error('Failed to subscribe');
    }
  };

  // Hooks must come before any conditional returns
  const heroArticle = useMemo(() => {
    if (layout.hero_source === 'latest') return latest[0];
    return featured[0] || latest[0];
  }, [layout.hero_source, featured, latest]);

  const secondaryArticles = useMemo(() => {
    if (!layout.show_secondary_strip) return [];
    if (layout.hero_source === 'latest') {
      const rest = latest.slice(1, 3);
      return rest.length ? rest : featured.slice(0, 2);
    }
    const fromFeat = featured.slice(1, 3);
    return fromFeat.length > 0 ? fromFeat : latest.slice(1, 3);
  }, [layout.show_secondary_strip, layout.hero_source, featured, latest]);

  const displayLatest = latest.filter((a) => a.id !== heroArticle?.id);

  if (loading) {
    return (
      <>
        <PublicHeader />
        <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8">
          <Skeleton className="h-96 w-full rounded-xl mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1,2,3].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <PublicHeader />
      <main className="bg-[#FBF7EF] min-h-screen">
        {/* Hero Section */}
        {layout.show_hero !== false && (
        <section className="max-w-[1200px] mx-auto px-4 sm:px-6 pt-8 pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Main Hero */}
            <div className="lg:col-span-8">
              {heroArticle ? (
                <ArticleCard article={heroArticle} variant="hero" />
              ) : (
                <div className="aspect-[16/9] bg-white rounded-xl flex items-center justify-center text-[#5B616E]">
                  <p>No featured articles yet. Create some in the admin dashboard.</p>
                </div>
              )}
            </div>

            {/* Secondary Stories */}
            <div className="lg:col-span-4 flex flex-col gap-4">
              {secondaryArticles.map(article => (
                <Link key={article.id} to={`/article/${article.id}`} data-testid="hero-secondary-article" className="news-card group block bg-white rounded-xl overflow-hidden border border-[#E7E0D6] flex-1">
                  {article.featured_image && (
                    <div className="aspect-[2/1] overflow-hidden">
                      <img src={article.featured_image} alt={article.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="p-4">
                    {article.category && (
                      <span className="news-kicker text-[10px] font-semibold" style={{ color: article.category.color || '#5B616E' }}>{article.category.name}</span>
                    )}
                    <h3 className="news-masthead text-base font-semibold leading-snug mt-1 group-hover:underline line-clamp-2">{article.title}</h3>
                  </div>
                </Link>
              ))}
              {layout.show_secondary_strip && secondaryArticles.length === 0 && (
                <div className="bg-white rounded-xl border border-[#E7E0D6] p-6 flex-1 flex items-center justify-center text-[#5B616E] text-sm">
                  More stories coming soon
                </div>
              )}
            </div>
          </div>
        </section>
        )}

        <Separator className="max-w-[1200px] mx-auto" />

        {/* Latest + Trending */}
        <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Latest */}
            {layout.show_latest !== false && (
            <div className={layout.show_trending_sidebar !== false ? 'lg:col-span-8' : 'lg:col-span-12'}>
              <div className="flex items-center gap-2 mb-6">
                <Clock className="w-5 h-5 text-[#B89B5E]" />
                <h2 className="news-masthead text-2xl font-bold">Latest News</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {displayLatest.slice(0, 6).map(article => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
              {displayLatest.length === 0 && (
                <div className="bg-white rounded-xl border border-[#E7E0D6] p-12 text-center text-[#5B616E]">
                  <p className="text-lg">No articles published yet</p>
                  <p className="text-sm mt-2">Articles will appear here once published from the admin dashboard.</p>
                </div>
              )}
            </div>
            )}

            {/* Sidebar: Trending + Newsletter */}
            {layout.show_trending_sidebar !== false && (
            <aside className="lg:col-span-4">
              {/* Trending */}
              <div className="bg-white rounded-xl border border-[#E7E0D6] p-5 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-[#B3261E]" />
                  <h3 className="font-semibold text-sm news-kicker">Trending Now</h3>
                </div>
                {trending.length > 0 ? (
                  <div className="divide-y divide-[#E7E0D6]">
                    {trending.map((article, idx) => (
                      <div key={article.id} className="py-3 first:pt-0">
                        <Link to={`/article/${article.id}`} className="group flex gap-3">
                          <span className="text-2xl font-bold text-[#E7E0D6] news-masthead">{String(idx + 1).padStart(2, '0')}</span>
                          <div>
                            <h4 className="text-sm font-medium leading-snug group-hover:underline line-clamp-2">{article.title}</h4>
                            <span className="text-xs text-[#5B616E] mt-1 block">{article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}</span>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#5B616E]">No trending articles yet</p>
                )}
              </div>

              {/* Newsletter */}
              <div className="bg-[#121316] rounded-xl p-5 text-white">
                <h3 className="font-semibold text-sm news-kicker text-white/90 mb-2">Stay Informed</h3>
                <p className="text-sm text-white/60 mb-4">Subscribe to our daily briefing and never miss a story.</p>
                <form onSubmit={handleSubscribe} className="flex flex-col gap-2">
                  <input
                    type="email"
                    value={newsletterEmail}
                    onChange={e => setNewsletterEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#B89B5E] placeholder:text-white/30"
                    required
                  />
                  <Button type="submit" className="bg-[#B89B5E] text-white hover:bg-[#A08A50] w-full">Subscribe</Button>
                </form>
              </div>
            </aside>
            )}
          </div>
        </section>

        {/* Category Sections */}
        {layout.show_category_rails !== false && categories.slice(0, Math.min(8, layout.category_rails_count ?? 3)).map(cat => {
          const arts = categoryArticles[cat.slug] || [];
          if (arts.length === 0) return null;
          return (
            <section key={cat.slug} className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8">
              <Separator className="mb-8" />
              <div className="flex items-center justify-between mb-6">
                <h2 className="news-masthead text-2xl font-bold flex items-center gap-2">
                  <span className="w-1 h-6 rounded-full" style={{ background: cat.color }} />
                  {cat.name}
                </h2>
                <Link to={`/category/${cat.slug}`} className="text-sm text-[#5B616E] hover:text-[#121316] flex items-center gap-1 transition-colors">
                  View All <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {arts.map(article => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </section>
          );
        })}
      </main>
      <PublicFooter />
    </>
  );
}
